import React, { useState } from 'react';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import analytics from '@ad-scripts/lib/analytics';
import customChannelRecipientResource, {
  CustomChannelRecipient,
} from 'src/actions/channels/customChannelRecipientResource';
import EmptyState from 'src/presentation/components/pages/proactiveCommunications/components/shared/EmptyState';
import { isLoadingStatus, STATUS_SUCCEEDED } from 'src/presentation/hooks/useResource';
import OverflowMenu, {
  OverflowMenuItem,
  OverflowMenuLink,
  usePopoverMenu,
} from 'src/presentation/components/pages/shared/OverflowMenu';
import RecipientIdEditor from './CustomChannelRecipientsEditor';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import { StyledConfirmOrCancelModal } from 'src/presentation/components/pages/shared/rowActions/StyledConfirmOrCancelModal';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import useRecipientCollections from './hooks/useRecipientCollections';
import useRouter from 'src/presentation/hooks/useRouter';
import ViewChannelIntegrationDataFormModal from './ViewChannelIntegrationDataFormModal';

const dataAidPrefix = 'recipients-';

const TooltipForName = name => (
  <Tooltip delay={750} message={name}>
    {name}
  </Tooltip>
);

const columnConfig = [
  { attr: 'name', label: 'Name', customRenderer: item => TooltipForName(get(item, 'name', '')) },
  { attr: 'configuration.recipientAddress', label: 'Entry Point Address' },
  { attr: 'updatedByString', label: 'Last Edited By' },
  { attr: 'updatedAtString', label: 'Last Edited On' },
];

const RecipientIds: TablePageRender = ({ root }) => {
  const { match }: { match: { url: string; params: { customChannelId: string } } } = useRouter() as any;

  const { customChannels, recipientCollections, loadingState, recipientIdsErrors } = useRecipientCollections({
    customChannelId: match.params.customChannelId,
  });

  const customChannel = find(customChannels, { id: match.params.customChannelId });

  const [endpoint, setEndpoint] = useState<CustomChannelRecipient>();

  const filterFunc = (items, searchString) => {
    if (!searchString) {
      return items;
    }
    const results = filter(items, item => {
      return (
        item.name.toLowerCase().includes(searchString.toLowerCase()) ||
        item.address.toLowerCase().includes(searchString.toLowerCase())
      );
    });
    return results;
  };

  const actions = (
    <RouterLinkButton data-aid={`${dataAidPrefix}addRecipients`} to={`${root}/new`}>
      Add Entry Point
    </RouterLinkButton>
  );

  const title = `${get(customChannel, 'name', '')} Entry Points`;

  const description = <HeaderDescription>Configure and manage the Entry Points for your Channel.</HeaderDescription>;

  const emptyStateRender = <EmptyState title="Set up the Entry Points for your Channel" actionsRender={actions} />;

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const dispatch = useDispatch();
  const handleDelete = () => {
    dispatch(
      customChannelRecipientResource.effects.remove({
        id: endpoint?.id,
        customChannelId: match.params.customChannelId,
        name: customChannel.name,
      })
    );
    setIsDeleteModalVisible(false);
  };
  const openDeleteModal = function (item) {
    setEndpoint(item);
    setIsDeleteModalVisible(true);
  };

  const closeDeleteModal = function () {
    setIsDeleteModalVisible(false);
  };

  const [isViewChannelIntegrationDataModalOpen, setIsViewChannelIntegrationDataModalOpen] = useState(false);
  const openViewChannelIntegrationDataModal = item => {
    setEndpoint(item);
    setIsViewChannelIntegrationDataModalOpen(true);
  };
  const closeViewChannelIntegrationDataModal = () => setIsViewChannelIntegrationDataModalOpen(false);

  return (
    <>
      <TablePage
        actions={actions}
        backButtonUrl={getBackUrl(root)}
        backButtonLabel="Back to Custom Channels"
        columnConfig={columnConfig}
        description={description}
        editor={RecipientIdEditor}
        errors={recipientIdsErrors}
        emptyStateFullHeightLayout
        emptyStateRender={emptyStateRender}
        filterFunc={filterFunc}
        isLoading={isLoadingStatus(loadingState)}
        items={recipientCollections}
        name={title}
        resetErrors={customChannelRecipientResource.actions.resetErrors}
        root={root}
        renderRowActions={item => (
          <RowActions
            item={item}
            root={root}
            openModal={openDeleteModal}
            openViewIntegrationDataModal={openViewChannelIntegrationDataModal}
          />
        )}
        data-aid={`${dataAidPrefix}page`}
      />
      {isDeleteModalVisible ? (
        <StyledConfirmOrCancelModal
          confirmationText="You sure about that?"
          deleteAction={customChannelRecipientResource.effects.remove}
          id={endpoint?.id}
          onCancel={closeDeleteModal}
          onSubmit={handleDelete}
          submitText="Yes, delete"
          text={
            <>
              You&apos;re about to delete <b>{endpoint?.name}</b>, and it cannot be undone.
            </>
          }
          title="You sure about that?"
        />
      ) : null}

      {isViewChannelIntegrationDataModalOpen && (
        <ViewChannelIntegrationDataFormModal
          onClose={closeViewChannelIntegrationDataModal}
          value={endpoint}
          loadingState={STATUS_SUCCEEDED}
        />
      )}
    </>
  );
};

const RowActions = ({ root, item, openModal, openViewIntegrationDataModal }) => {
  const { item: endpoint } = item;
  const { onClose, ...popoverMenuProps } = usePopoverMenu();

  return (
    <OverflowMenu id={endpoint.id} onClose={onClose} {...popoverMenuProps} tooltipMessage="More options">
      <OverflowMenuItem id="editId">
        <OverflowMenuLink to={`${root}/${endpoint.id}`}>Edit Entry Point</OverflowMenuLink>
      </OverflowMenuItem>
      <OverflowMenuItem id="viewIntegrationData">
        <OverflowMenuLink
          onClick={() => {
            analytics.track('Admin - Custom Channels - Recipients - View Integration Data', {
              endpointId: item.address,
            });
            onClose();
            openViewIntegrationDataModal(endpoint);
          }}
          to="#"
        >
          View Integration Data
        </OverflowMenuLink>
      </OverflowMenuItem>
      <OverflowMenuItem id="deleteId">
        <OverflowMenuLink
          onClick={() => {
            analytics.track('Admin - Custom Channels - Recipients - Delete', { endpointId: item.address });
            onClose();
            openModal(endpoint);
          }}
          to="#"
        >
          Delete Entry Point
        </OverflowMenuLink>
      </OverflowMenuItem>
    </OverflowMenu>
  );
};

const HeaderDescription = styled.div`
  display: inline;
`;

const getBackUrl = root => {
  const parts = root.split('/');
  parts.splice(parts.length - 2, 2);
  return parts.join('/');
};

export default RecipientIds;
